import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Timer, TrendingUp, Trophy, Weight } from 'lucide-react';
import { assessments, workoutExecution } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import { recordGain, summarize, weekStart, weeklyBuckets, weekSummary, progressionPoints } from '../utils/trainingStats';
import { formatDuration, plural } from '../components/WorkoutPlayer/playerLogic';
import '../styles/ui.css';

/*
 * Progresso — a evolução do sócio, medida. Não repete o Início (que diz o que fazer hoje) nem o Treino > Histórico (a lista de cada treino).
 *   Treino   quantos treinos, quanto tempo e volume, a regularidade por semana e os recordes por exercício (a partir dos treinos concluídos)
 *   Corpo    as medições registadas pelo ginásio (peso, gordura, massa muscular, cintura)
 * Cada separador só existe se o ginásio tem o módulo ligado (Treino / Progresso do sócio) e o perfil tem acesso.
 */

const BODY_METRICS = [
  { key: 'weight', label: 'Peso', unit: 'kg' },
  { key: 'bodyfat', label: 'Gordura', unit: '%' },
  { key: 'muscle', label: 'Massa muscular', unit: 'kg' },
  { key: 'waist', label: 'Cintura', unit: 'cm' },
];

const fmtKg = (n) => `${Math.round(Number(n) || 0).toLocaleString('pt-PT')} kg`;
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' }) : '');

export default function ClientProgressPage() {
  const { hasPermission } = useAuth();
  const { isEnabled } = useCapabilities();
  const hasTraining = isEnabled('TrainingPlans') && hasPermission('canAccessWorkoutPlans');
  const hasBody = isEnabled('ClientProgress');
  const [tab, setTab] = useState(hasTraining ? 'treino' : 'corpo');
  const active = tab === 'treino' && hasTraining ? 'treino' : hasBody ? 'corpo' : 'treino';

  if (!hasTraining && !hasBody) {
    return <div className="gm-page"><div className="gm-empty"><strong>O progresso não está disponível</strong>Este ginásio não tem este módulo ligado.</div></div>;
  }

  return (
    <div className="gm-page" data-testid="client-progress">
      {hasTraining && hasBody ? (
        <div className="gm-tabs" role="tablist" aria-label="Progresso">
          <button type="button" role="tab" aria-selected={active === 'treino'} className={`gm-tab ${active === 'treino' ? 'is-on' : ''}`} onClick={() => setTab('treino')}>Treino</button>
          <button type="button" role="tab" aria-selected={active === 'corpo'} className={`gm-tab ${active === 'corpo' ? 'is-on' : ''}`} onClick={() => setTab('corpo')}>Corpo</button>
        </div>
      ) : null}
      {active === 'treino' ? <TrainingProgress /> : <BodyProgress />}
    </div>
  );
}

// ── Treino ────────────────────────────────────────────────────────────────

const PAGE = 100;
const MAX_PAGES = 4;   // 400 treinos chegam para a regularidade e os totais recentes

function TrainingProgress() {
  const [state, setState] = useState('loading');
  const [items, setItems] = useState([]);
  const [records, setRecords] = useState([]);
  const [recordsFailed, setRecordsFailed] = useState(false);
  const [muscles, setMuscles] = useState(null);        // séries por músculo, esta semana (calculada como as barras)
  const [musclesFailed, setMusclesFailed] = useState(false);
  const [reload, setReload] = useState(0);   // «Tentar de novo» volta a correr o pedido

  useEffect(() => {
    let alive = true;
    let recordsFailed = false;
    let musclesFailed = false;
    setState('loading');
    (async () => {
      try {
        let all = [];
        for (let page = 0; page < MAX_PAGES; page += 1) {
          const r = await workoutExecution.history(page * PAGE, PAGE);
          const got = r.data?.items || [];
          all = all.concat(got);
          if (got.length < PAGE || all.length >= (r.data?.total || 0)) break;
        }
        const rec = await workoutExecution.records()
          .then((r) => r.data?.items || [])
          // Os recordes não são essenciais para o resto do ecrã, mas uma falha não pode passar por
          // «ainda não tem recordes» — a secção diz que não conseguiu carregá-los.
          .catch(() => { recordsFailed = true; return []; });
        // A semana é a MESMA das barras (segunda a domingo, na hora do atleta): duas contas diferentes dariam
        // números a discordar sobre a mesma semana.
        const weekFrom = weekStart(new Date());
        const weekTo = new Date(weekFrom.getTime() + 7 * 24 * 60 * 60 * 1000 - 1);
        const muscleSets = await workoutExecution.weeklySets(weekFrom.toISOString(), weekTo.toISOString())
          .then((r) => r.data || null)
          .catch(() => { musclesFailed = true; return null; });
        if (!alive) return;
        setItems(all); setRecords(rec); setRecordsFailed(recordsFailed);
        setMuscles(muscleSets); setMusclesFailed(musclesFailed); setState('ok');
      } catch {
        if (alive) setState('error');
      }
    })();
    return () => { alive = false; };
  }, [reload]);

  const now = useMemo(() => new Date(), []);
  const summary = useMemo(() => summarize(items, now), [items, now]);
  const buckets = useMemo(() => weeklyBuckets(items, now, 8), [items, now]);
  const week = useMemo(() => weekSummary(items, now), [items, now]);

  if (state === 'loading') return <div className="gm-skeleton" aria-busy="true" aria-label="A carregar o progresso" />;
  if (state === 'error') return <div className="gm-error" role="alert">Não foi possível carregar o progresso do treino.</div>;
  if (summary.total === 0) {
    return (
      <div className="gm-empty" data-testid="progress-empty">
        <strong>Ainda não há treinos concluídos</strong>
        Depois do primeiro treino, aqui vê os totais, a regularidade e os seus recordes.
        <div><Link className="gm-btn gm-btn-primary" to="/meu-treino">Ir para o treino de hoje</Link></div>
      </div>
    );
  }

  const { last30 } = summary;
  return (
    <div className="gm-grid" data-testid="progress-training">
      <section className="gm-card gm-col-12" aria-label="Últimos 30 dias">
        <div className="gm-card-head"><h3 className="gm-card-title">Últimos 30 dias</h3></div>
        <div className="gm-grid">
          <div className="gm-col-3"><Stat icon={TrendingUp} value={last30.sessions} label={last30.sessions === 1 ? 'treino' : 'treinos'} /></div>
          <div className="gm-col-3"><Stat icon={Timer} value={formatDuration(last30.minutes * 60)} label="a treinar" /></div>
          <div className="gm-col-3"><Stat icon={Weight} value={fmtKg(last30.volumeKg)} label="de volume" /></div>
          <div className="gm-col-3"><Stat icon={Flame} value={summary.streakWeeks} label={summary.streakWeeks === 1 ? 'semana seguida' : 'semanas seguidas'} /></div>
        </div>
      </section>

      <section className="gm-card gm-col-12" aria-label="Resumo da semana" data-testid="week-summary">
        <div className="gm-card-head"><h3 className="gm-card-title">Resumo da semana</h3></div>
        <div className="gm-grid">
          <div className="gm-col-4"><Stat icon={TrendingUp} value={week.sessions} label={week.sessions === 1 ? 'treino esta semana' : 'treinos esta semana'} /></div>
          <div className="gm-col-4"><Stat icon={Weight} value={week.sets} label={week.sets === 1 ? 'série registada' : 'séries registadas'} /></div>
          <div className="gm-col-4"><Stat icon={Weight} value={fmtKg(week.volumeKg)} label="de volume" /></div>
        </div>
        <p className="gm-note" data-testid="week-note">
          De segunda a domingo. Volume = soma da carga × repetições das séries com carga
          {week.sessions > 0 ? `, em ${plural(week.sessions, 'treino concluído', 'treinos concluídos')}` : ''}.
          {summary.streakWeeks > 0 ? ` Vai em ${plural(summary.streakWeeks, 'semana seguida com pelo menos um treino', 'semanas seguidas com pelo menos um treino')}.` : ''}
        </p>
      </section>

      <section className="gm-card gm-col-7" aria-label="Regularidade por semana">
        <div className="gm-card-head"><h3 className="gm-card-title">Regularidade · últimas 8 semanas</h3></div>
        <WeekBars buckets={buckets} />
      </section>

      <section className="gm-card gm-col-5" aria-label="Recordes">
        <div className="gm-card-head"><h3 className="gm-card-title">Os seus recordes</h3></div>
        {recordsFailed ? (
          <p className="gm-note" role="alert" data-testid="records-failed">Não foi possível carregar os recordes. <button type="button" className="gm-btn gm-btn-ghost" onClick={() => setReload((n) => n + 1)}>Tentar de novo</button></p>
        ) : records.length === 0 ? <p className="gm-note">Os recordes aparecem quando registar treinos com carga.</p> : (
          <ul className="gm-list" data-testid="records">
            {records.slice(0, 6).map((r) => <RecordRow key={`${r.libraryExerciseId ?? r.name}`} record={r} />)}
          </ul>
        )}
        {!recordsFailed && records.length > 0 ? (
          <p className="gm-note" data-testid="onerm-note">
            O <strong>1RM estimado</strong> é uma estimativa (fórmula de Epley) a partir da melhor série que registou — serve de
            referência para escolher cargas, não é um teste de esforço máximo.
          </p>
        ) : null}
      </section>

      <section className="gm-card gm-col-12" aria-label="Grupos musculares esta semana">
        <div className="gm-card-head"><h3 className="gm-card-title">Grupos musculares · esta semana</h3></div>
        {musclesFailed ? (
          <p className="gm-note" role="alert" data-testid="muscles-failed">
            Não foi possível carregar as séries por músculo.
            {' '}<button type="button" className="gm-btn gm-btn-ghost" onClick={() => setReload((n) => n + 1)}>Tentar de novo</button>
          </p>
        ) : !muscles || muscles.totalSets === 0 ? (
          <p className="gm-note" data-testid="muscles-empty">Ainda não registou séries esta semana.</p>
        ) : (
          <>
            <ul className="gm-list" data-testid="muscles">
              {muscles.muscles.map((m) => (
                <li key={m.muscleId} className="gm-list-row">
                  <span className="gm-list-main">
                    <span className="gm-list-title">{m.name}</span>
                    <span className="gm-list-sub">{plural(m.exercises, 'exercício', 'exercícios')}</span>
                  </span>
                  <span className="gm-chip">{plural(m.sets, 'série', 'séries')}</span>
                </li>
              ))}
            </ul>
            <p className="gm-note" data-testid="muscles-note">
              Séries contadas pelos músculos principais dos exercícios que fez esta semana, do grupo mais treinado para o menos.
              {muscles.unattributedSets > 0 ? ` ${plural(muscles.unattributedSets, 'série', 'séries')} em exercícios sem ligação à biblioteca não ${muscles.unattributedSets === 1 ? 'entra' : 'entram'} por músculo.` : ''}
              {' '}A meta por músculo ainda não está definida — mostra-se o que fez, não uma percentagem sem regra.
            </p>
          </>
        )}
      </section>

      <p className="gm-note gm-col-12">
        {plural(summary.total, 'treino concluído', 'treinos concluídos')} no total. <Link className="gm-card-link" to="/meu-treino/historico">Ver o histórico →</Link>
      </p>
    </div>
  );
}

/**
 * Um recorde, e (ao abrir) a evolução REAL desse exercício: as últimas sessões com a carga máxima e o volume, e a comparação entre a
 * mais recente e a anterior. Só se compara o que existe: com uma sessão, não há comparação.
 */
function RecordRow({ record: r }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ status: 'idle', data: null });
  const gain = recordGain(r);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && state.status === 'idle' && r.libraryExerciseId) {
      setState({ status: 'loading', data: null });
      workoutExecution.exerciseHistory(r.libraryExerciseId, 12)
        .then((res) => setState({ status: 'ok', data: res.data }))
        .catch(() => setState({ status: 'error', data: null }));
    }
  };

  const sessions = state.data?.sessions || [];
  const points = progressionPoints(state.data);
  const [latest, previous] = sessions;
  const delta = latest && previous && latest.topWeightKg > 0 && previous.topWeightKg > 0 ? Number(latest.topWeightKg) - Number(previous.topWeightKg) : null;

  return (
    <li className="gm-list-row" style={{ flexWrap: 'wrap' }} data-testid="record-row">
      <span className="gm-pick-mark" aria-hidden="true" style={{ border: 0, color: r.recent ? 'var(--accent-primary)' : 'var(--text-secondary)' }}><Trophy size={16} /></span>
      <span className="gm-list-main">
        <span className="gm-list-title gm-list-name">{r.name}</span>
        <span className="gm-list-sub">{`${Number(r.bestWeightKg).toLocaleString('pt-PT')} kg × ${r.bestReps}`} · {fmtDay(r.achievedAt)} · {plural(r.sessions, 'treino', 'treinos')}{r.estimatedOneRepMaxKg ? ` · 1RM estimado ${Number(r.estimatedOneRepMaxKg).toLocaleString('pt-PT')} kg` : ''}</span>
      </span>
      {gain ? <span className="gm-chip is-good">+{gain.toLocaleString('pt-PT')} kg</span> : null}
      {r.libraryExerciseId ? (
        <button type="button" className="gm-card-link" onClick={toggle} aria-expanded={open} aria-label={`Evolução de ${r.name}`}>
          {open ? 'Fechar' : 'Evolução'}
        </button>
      ) : null}
      {open ? (
        <div style={{ flexBasis: '100%' }} data-testid="record-history">
          {state.status === 'loading' ? <p className="gm-note" role="status">A carregar…</p> : null}
          {state.status === 'error' ? <p className="gm-note" role="alert">Não foi possível carregar a evolução.</p> : null}
          {state.status === 'ok' && sessions.length === 0 ? <p className="gm-note" data-testid="record-history-empty">Ainda não há histórico suficiente para mostrar a evolução.</p> : null}
          {sessions.length > 0 ? (
            <>
              <ExerciseChart points={points} />
              {delta != null ? (
                <p className="gm-note" data-testid="record-delta">
                  Última sessão: carga máxima {delta === 0 ? 'igual à anterior' : `${delta > 0 ? '+' : '−'}${Math.abs(delta).toLocaleString('pt-PT')} kg face à anterior`}.
                </p>
              ) : <p className="gm-note">Com mais uma sessão com carga já se pode comparar.</p>}
              <ul className="gm-list">
                {sessions.map((s) => (
                  <li key={s.workoutExecutionId} className="gm-list-row">
                    <span className="gm-list-main">
                      <span className="gm-list-title">{fmtDay(s.performedAt)}</span>
                      <span className="gm-list-sub">
                        {s.sets.map((x) => (x.weightKg ? `${Number(x.weightKg).toLocaleString('pt-PT')}×${x.reps}` : `${x.reps || x.durationSeconds}`)).join('  ·  ')}
                        {s.volumeKg > 0 ? ` · volume ${fmtKg(s.volumeKg)}` : ''}
                        {s.estimatedOneRepMaxKg ? ` · 1RM ~${Number(s.estimatedOneRepMaxKg).toLocaleString('pt-PT')} kg` : ''}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

const Stat = ({ icon: Icon, value, label }) => (
  <div className="gm-stat">
    <Icon size={18} aria-hidden="true" style={{ color: 'var(--accent-primary)' }} />
    <span className="gm-stat-value">{value}</span>
    <span className="gm-stat-label">{label}</span>
  </div>
);

/**
 * A evolução de UM exercício, desenhada em SVG (sem biblioteca de gráficos — o repo não tem nenhuma): a carga máxima de
 * cada sessão (linha cheia) e o 1RM estimado (linha tracejada, a mesma conta de Epley do resto do ecrã). Sem pelo menos
 * DUAS sessões com carga não se desenha nada — com uma só não há evolução, com zero não há nada para ler — e o ecrã
 * mostra o estado vazio, nunca um gráfico de zeros. Para quem usa leitor de ecrã, a tabela escondida diz os mesmos pontos.
 */
function ExerciseChart({ points }) {
  const withLoad = points.filter((p) => p.topWeightKg != null);
  if (withLoad.length < 2) return null;

  const values = points.flatMap((p) => [p.topWeightKg, p.oneRepMaxKg]).filter((v) => v != null);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const W = 660; const H = 240; const padL = 46; const padR = 16; const padT = 30; const padB = 44;
  const n = points.length;
  const x = (i) => padL + (i / (n - 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - min) / span) * (H - padT - padB);
  const path = (key) => points.map((p, i) => (p[key] != null ? `${x(i).toFixed(1)},${y(p[key]).toFixed(1)}` : null)).filter(Boolean).join(' ');
  const label = (i) => new Date(points[i].performedAt).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
  const dateIdx = n <= 6 ? points.map((_, i) => i) : [0, n - 1];

  return (
    <figure className="gm-col-12" style={{ margin: '10px 0 4px', padding: 0 }} data-testid="exercise-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Evolução da carga máxima e do 1RM estimado" style={{ display: 'block' }}>
        <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="var(--border-color)" strokeWidth="1" />
        <text x={padL - 8} y={padT + 4} textAnchor="end" fontSize="13" fill="var(--text-secondary)">{Math.round(max)}</text>
        <text x={padL - 8} y={H - padB} textAnchor="end" fontSize="13" fill="var(--text-secondary)">{Math.round(min)}</text>
        <polyline points={path('topWeightKg')} fill="none" stroke="var(--accent-primary)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        <polyline points={path('oneRepMaxKg')} fill="none" stroke="var(--text-secondary)" strokeWidth="2.5" strokeDasharray="6 5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <g key={p.performedAt}>
            {p.topWeightKg != null ? <circle cx={x(i)} cy={y(p.topWeightKg)} r="4.5" fill="var(--bg-card)" stroke="var(--accent-primary)" strokeWidth="2.5" /> : null}
            {p.oneRepMaxKg != null ? <circle cx={x(i)} cy={y(p.oneRepMaxKg)} r="3.5" fill="var(--bg-card)" stroke="var(--text-secondary)" strokeWidth="2" /> : null}
          </g>
        ))}
        {dateIdx.map((i) => (
          <text key={i} x={x(i)} y={H - 12} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize="13" fill="var(--text-secondary)">{label(i)}</text>
        ))}
      </svg>
      <figcaption className="gm-note" style={{ marginTop: 4 }}>
        <span style={{ color: 'var(--accent-primary)', fontWeight: 700 }}>——</span> carga máxima da sessão ·{' '}
        <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>┈┈</span> 1RM estimado (Epley)
      </figcaption>
      <table className="gm-sr-only">
        <caption>Evolução por sessão</caption>
        <thead><tr><th>Data</th><th>Carga máxima (kg)</th><th>1RM estimado (kg)</th></tr></thead>
        <tbody>{points.map((p) => <tr key={p.performedAt}><td>{new Date(p.performedAt).toLocaleDateString('pt-PT')}</td><td>{p.topWeightKg ?? ''}</td><td>{p.oneRepMaxKg ?? ''}</td></tr>)}</tbody>
      </table>
    </figure>
  );
}

/** Treinos por semana, em barras. O gráfico é decorativo para quem usa leitor de ecrã: a tabela escondida diz os mesmos números. */
function WeekBars({ buckets }) {
  const max = Math.max(1, ...buckets.map((b) => b.sessions));
  const W = 640; const H = 170; const pad = 22; const gap = 10;
  const bw = (W - pad * 2 - gap * (buckets.length - 1)) / buckets.length;
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" aria-hidden="true" style={{ display: 'block' }} data-testid="week-bars">
        {buckets.map((b, i) => {
          const h = b.sessions === 0 ? 3 : Math.max(8, (b.sessions / max) * (H - 54));
          const x = pad + i * (bw + gap);
          const y = H - 28 - h;
          const current = i === buckets.length - 1;
          return (
            <g key={b.start.toISOString()}>
              <rect x={x} y={y} width={bw} height={h} rx="6" fill={b.sessions === 0 ? 'var(--border-color)' : 'var(--accent-primary)'} opacity={current ? 1 : 0.8} />
              {b.sessions > 0 ? <text x={x + bw / 2} y={y - 6} textAnchor="middle" fontSize="13" fontWeight="700" fill="var(--text-primary)">{b.sessions}</text> : null}
              <text x={x + bw / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--text-secondary)">{b.start.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}</text>
            </g>
          );
        })}
      </svg>
      <table className="gm-sr-only">
        <caption>Treinos por semana</caption>
        <thead><tr><th>Semana de</th><th>Treinos</th></tr></thead>
        <tbody>{buckets.map((b) => <tr key={b.start.toISOString()}><td>{b.start.toLocaleDateString('pt-PT')}</td><td>{b.sessions}</td></tr>)}</tbody>
      </table>
    </>
  );
}

// ── Corpo ─────────────────────────────────────────────────────────────────
// As medições registadas pelo ginásio: série temporal + inicial/atual/diferença. Sem juízo bom/mau, sem conclusões médicas.

function BodyProgress() {
  const [metric, setMetric] = useState('weight');
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    setState('loading');
    assessments.myProgress(metric)
      .then((r) => { setData(r.data); setState('ok'); })
      .catch((e) => setState(e?.response?.status === 403 ? 'disabled' : 'error'));
  }, [metric]);

  if (state === 'disabled') return <div className="gm-empty"><strong>As medições não estão disponíveis</strong>Este ginásio não tem o módulo ligado.</div>;

  const series = data?.series || [];
  const unit = BODY_METRICS.find((m) => m.key === metric)?.unit || '';

  return (
    <>
      <div className="gm-tabs" role="tablist" aria-label="Indicador">
        {BODY_METRICS.map((m) => (
          <button key={m.key} type="button" role="tab" aria-selected={metric === m.key} className={`gm-tab ${metric === m.key ? 'is-on' : ''}`} onClick={() => setMetric(m.key)}>{m.label}</button>
        ))}
      </div>

      {state === 'loading' ? <div className="gm-skeleton" aria-busy="true" aria-label="A carregar" />
        : state === 'error' ? <div className="gm-error" role="alert">Não foi possível carregar as medições.</div>
        : series.length === 0 ? <div className="gm-empty"><strong>Ainda sem medições</strong>Quando o ginásio registar uma avaliação, a sua evolução aparece aqui.</div>
        : (
          <>
            <section className="gm-card" aria-label="Resumo">
              <div className="gm-grid">
                <div className="gm-col-4"><BodyStat label="Inicial" value={`${Number(data.first)} ${unit}`} /></div>
                <div className="gm-col-4"><BodyStat label="Atual" value={`${Number(data.last)} ${unit}`} /></div>
                <div className="gm-col-4"><BodyStat label="Diferença" value={`${Number(data.delta) > 0 ? '+' : ''}${Number(data.delta).toFixed(1)} ${unit}`} /></div>
              </div>
            </section>
            {series.length >= 2 && <LineChart series={series} />}
          </>
        )}
    </>
  );
}

const BodyStat = ({ label, value }) => (<div className="gm-stat"><span className="gm-stat-value">{value}</span><span className="gm-stat-label">{label}</span></div>);

// SVG line chart simples, legível em mobile e que segue o tema. Sem interpretação de valor.
function LineChart({ series }) {
  const vals = series.map((p) => Number(p.value));
  const min = Math.min(...vals); const max = Math.max(...vals);
  const range = max - min || 1;
  const W = 640; const H = 200; const pad = 14;
  const point = (v, i) => [pad + (i / (vals.length - 1)) * (W - pad * 2), pad + (1 - (v - min) / range) * (H - pad * 2)];
  const pts = vals.map((v, i) => point(v, i).map((n) => n.toFixed(1)).join(',')).join(' ');
  return (
    <section className="gm-card" aria-label="Evolução ao longo do tempo">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Evolução ao longo do tempo" style={{ display: 'block' }}>
        <polyline points={pts} fill="none" stroke="var(--accent-primary)" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        {vals.map((v, i) => { const [x, y] = point(v, i); return <circle key={i} cx={x} cy={y} r="4" fill="var(--bg-card)" stroke="var(--accent-primary)" strokeWidth="2.5" />; })}
      </svg>
    </section>
  );
}
